import React, { useState, useEffect } from 'react';
import {
  UserCheck,
  Plus,
  Edit3,
  Trash2,
  Search,
  Filter,
  Calendar,
  Clock,
  Users,
  DollarSign,
  CheckCircle,
  XCircle,
  AlertCircle,
  Mail,
  Phone,
  X,
  Eye,
  User,
  BookOpen,
  ClipboardList,
  Ban
} from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { useEnrollments } from './EnrollmentsProvider';
import classSessionsService from '../../services/classSessionsService';
import api from '../../services/api';

// Individual Enrollment Row Component
const EnrollmentRow = ({ enrollment, isSelected, onSelect, onEdit, onDelete, onView, onEmail }) => {
  const [sessions, setSessions] = useState([]);
  const [sessionsLoading, setSessionsLoading] = useState(true);

  // Load sessions for this class (same logic as ClassCard)
  useEffect(() => {
    const loadSessions = async () => {
      // Try different possible field names for class ID
      const classId = enrollment.class_id || enrollment.classId || enrollment.id;
      
      if (!classId) {
        setSessionsLoading(false);
        return;
      }
      
      try {
        setSessionsLoading(true);
        const result = await classSessionsService.getSessionsForClass(classId);
        if (result.success) {
          setSessions(result.data || []);
        } else {
          console.error('Session service returned error:', result.error);
          setSessions([]);
        }
      } catch (error) {
        console.error('Failed to load sessions for class:', classId, error);
        setSessions([]);
      } finally {
        setSessionsLoading(false);
      }
    };

    loadSessions();
  }, [enrollment.class_id, enrollment.classId, enrollment.id]);
  const getPaymentStatusColor = (status) => {
    switch (status) {
      case 'paid': return 'bg-emerald-100 text-emerald-700';
      case 'partial': return 'bg-[#FFECC5] text-[#3C3C3C]';
      case 'pending': return 'bg-[#F5F5F5] text-[#3C3C3C]';
      case 'overdue': return 'bg-red-100 text-red-700';
      default: return 'bg-[#F5F5F5] text-[#3C3C3C]';
    }
  };

  const getEnrollmentStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'active': return 'bg-blue-100 text-blue-700';
      case 'completed': return 'bg-gray-100 text-gray-700';
      case 'cancelled': return 'bg-red-100 text-red-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  // Format session information for display (same as ClassCard)
  const formatSessionInfo = () => {
    if (sessionsLoading) {
      return 'Loading...';
    }
    
    if (!sessions || sessions.length === 0) {
      return 'No sessions scheduled';
    }

    // Sort sessions by date
    const sortedSessions = [...sessions].sort((a, b) => new Date(a.session_date) - new Date(b.session_date));
    const firstSession = sortedSessions[0];
    const sessionCount = sessions.length;
    
    if (sessionCount === 1) {
      return formatDate(firstSession.session_date);
    } else {
      const firstDate = formatDate(firstSession.session_date);
      return `${sessionCount} sessions, starts ${firstDate}`;
    }
  };

  const studentName = enrollment.studentName || `${enrollment.studentFirstName || ''} ${enrollment.studentLastName || ''}`.trim();
  const className = enrollment.className || enrollment.class_name || 'N/A';
  const gender = enrollment.studentGender || enrollment.gender || '';

  // Get gender-specific placeholder background
  const getGenderPlaceholderStyle = () => {
    switch (gender?.toLowerCase()) {
      case 'female':
        return 'bg-gradient-to-br from-pink-300 to-pink-500';
      case 'male':
        return 'bg-gradient-to-br from-blue-300 to-blue-500';
      default:
        return 'bg-gradient-to-br from-gray-300 to-gray-500';
    }
  };

  return (
    <div className="relative">
      <div className="flex items-center px-4 py-2 border-b border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
        {/* Selection Checkbox */}
        <input
          type="checkbox"
          checked={isSelected}
          onChange={(e) => onSelect(enrollment.id, e.target.checked)}
          className="w-4 h-4 text-[#7A99D8] border-gray-300 dark:border-gray-600 rounded focus:ring-[#7A99D8] focus:ring-2 mr-3 dark:bg-gray-700"
        />

        {/* Student Photo */}
        <div className="flex-shrink-0 mr-3">
          {/* Try multiple possible field names for student photo */}
          {(enrollment.studentPhoto || enrollment.student_photo || enrollment.photoUrl || enrollment.photo_url || enrollment.photo) ? (
            <img
              src={enrollment.studentPhoto || enrollment.student_photo || enrollment.photoUrl || enrollment.photo_url || enrollment.photo}
              alt={studentName}
              className="w-8 h-8 rounded-lg object-cover"
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'flex';
              }}
            />
          ) : null}
          <div 
            className={`w-8 h-8 ${getGenderPlaceholderStyle()} rounded-lg flex items-center justify-center text-white text-xs font-medium`}
            style={{ display: (enrollment.studentPhoto || enrollment.student_photo || enrollment.photoUrl || enrollment.photo_url || enrollment.photo) ? 'none' : 'flex' }}
          >
            {(enrollment.student_first_name || enrollment.studentFirstName || '').charAt(0)}{(enrollment.student_last_name || enrollment.studentLastName || '').charAt(0)}
          </div>
        </div>

        {/* Student Name */}
        <div className="w-32 lg:w-32 flex-shrink-0 mr-4">
          <h3 className="text-sm font-medium text-[#3C3C3C] dark:text-gray-100 truncate">
            {studentName}
          </h3>
          {/* Mobile-only status and payment info */}
          <div className="lg:hidden flex items-center gap-2 mt-0.5 flex-wrap">
            <span className={`px-1.5 py-0.5 text-xs font-medium rounded-full ${getEnrollmentStatusColor(enrollment.enrollmentStatus || enrollment.enrollment_status)}`}>
              {(enrollment.enrollmentStatus || enrollment.enrollment_status || 'active')?.charAt(0).toUpperCase() + (enrollment.enrollmentStatus || enrollment.enrollment_status || 'active')?.slice(1)}
            </span>
            <span className={`px-1.5 py-0.5 text-xs font-medium rounded-full ${getPaymentStatusColor(enrollment.paymentStatus)}`}>
              {enrollment.paymentStatus?.charAt(0).toUpperCase() + enrollment.paymentStatus?.slice(1)}
            </span>
            <span className="text-xs text-gray-600 dark:text-gray-400">${enrollment.total_amount || enrollment.classPrice || 0}</span>
          </div>
        </div>

        {/* Class Info Group */}
        <div className="flex-1 min-w-0 mr-4">
          <div className="flex items-center gap-4">
            <div className="flex-1 min-w-0">
              <p className="text-sm text-gray-900 dark:text-gray-100 truncate">{className}</p>
              <div className="flex items-center text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                <Calendar className="w-3 h-3 mr-1" />
                <span className="truncate">{formatSessionInfo()}</span>
                {/* Mobile-only enrollment date */}
                <span className="lg:hidden ml-2 text-gray-400 dark:text-gray-500">
                  • Enrolled {formatDate(enrollment.enrollmentDate)}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Enrollment Status - Desktop only */}
        <div className="w-24 flex-shrink-0 mr-4 hidden lg:block">
          <span className={`px-1.5 py-0.5 text-xs font-medium rounded-full ${getEnrollmentStatusColor(enrollment.enrollmentStatus || enrollment.enrollment_status)}`}>
            {(enrollment.enrollmentStatus || enrollment.enrollment_status || 'active')?.charAt(0).toUpperCase() + (enrollment.enrollmentStatus || enrollment.enrollment_status || 'active')?.slice(1)}
          </span>
        </div>

        {/* Payment Status - Desktop only */}
        <div className="w-24 flex-shrink-0 mr-4 hidden lg:block">
          <span className={`px-1.5 py-0.5 text-xs font-medium rounded-full ${getPaymentStatusColor(enrollment.paymentStatus)}`}>
            {enrollment.paymentStatus?.charAt(0).toUpperCase() + enrollment.paymentStatus?.slice(1)}
          </span>
        </div>

        {/* Price - Desktop only */}
        <div className="w-20 flex-shrink-0 mr-4 hidden lg:block">
          <p className="text-sm font-medium text-gray-900 dark:text-gray-100">${enrollment.total_amount || enrollment.classPrice || 0}</p>
        </div>

        {/* Date Info Group - Desktop only */}
        <div className="w-20 flex-shrink-0 mr-4 hidden lg:block">
          <p className="text-xs text-gray-600 dark:text-gray-400">{formatDate(enrollment.enrollmentDate)}</p>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-0.5">
          <button
            onClick={() => onView(enrollment)}
            className="p-1.5 text-gray-400 dark:text-gray-500 hover:text-[#7A99D8] dark:hover:text-[#7A99D8] rounded hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            title="View Details"
          >
            <Eye className="w-3.5 h-3.5" />
          </button>
          <button
            onClick={() => onEdit(enrollment)}
            className="p-1.5 text-gray-400 dark:text-gray-500 hover:text-[#7A99D8] dark:hover:text-[#7A99D8] rounded hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            title="Edit Enrollment"
          >
            <Edit3 className="w-3.5 h-3.5" />
          </button>
          <button
            onClick={() => onDelete(enrollment)}
            className="p-1.5 text-gray-400 dark:text-gray-500 hover:text-red-600 dark:hover:text-red-400 rounded hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
            title="Cancel Enrollment"
          >
            <Ban className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
};

// Main Enrollments Component
const Enrollments = () => {
  const navigate = useNavigate();
  const {
    enrollments,
    loading,
    error,
    fetchEnrollments,
    cancelEnrollment,
    cancelEnrollments,
    clearError
  } = useEnrollments();

  const [activeTab, setActiveTab] = useState('classes');
  const [blockEnrollments, setBlockEnrollments] = useState([]);
  const [blockEnrollmentsLoading, setBlockEnrollmentsLoading] = useState(false);
  const [waitlistEnrollments, setWaitlistEnrollments] = useState([]);
  const [waitlistLoading, setWaitlistLoading] = useState(false);
  const [selectedEnrollments, setSelectedEnrollments] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [sortBy, setSortBy] = useState(() => {
    return localStorage.getItem('enrollments-sortBy') || 'enrollmentDate';
  });
  const [sortOrder, setSortOrder] = useState(() => {
    return localStorage.getItem('enrollments-sortOrder') || 'desc';
  });
  const [filters, setFilters] = useState({
    studentName: '',
    className: '',
    instructor: '',
    paymentStatus: '',
    enrollmentStatus: 'active', // Default to active enrollments only
    dateFrom: '',
    dateTo: ''
  });

  // Fetch learning block enrollments
  const fetchBlockEnrollments = async () => {
    try {
      setBlockEnrollmentsLoading(true);
      console.log('Fetching learning block enrollments...');
      const response = await api.request('/blocks/enrollments');
      console.log('Learning block enrollments response:', response);
      console.log('Learning block enrollments data:', response.enrollments);
      setBlockEnrollments(response.enrollments || []);
    } catch (err) {
      console.error('Error fetching learning block enrollments:', err);
      setBlockEnrollments([]);
    } finally {
      setBlockEnrollmentsLoading(false);
    }
  };

  // Fetch waitlist enrollments
  const fetchWaitlistEnrollments = async () => {
    try {
      setWaitlistLoading(true);
      console.log('Fetching waitlist enrollments...');
      const response = await api.request('/enrollments/waitlist');
      console.log('Waitlist enrollments response:', response);
      setWaitlistEnrollments(response.waitlist || []);
    } catch (err) {
      console.error('Error fetching waitlist enrollments:', err);
      setWaitlistEnrollments([]);
    } finally {
      setWaitlistLoading(false);
    }
  };

  // Load enrollments on component mount
  useEffect(() => {
    fetchEnrollments();
    fetchBlockEnrollments();
    fetchWaitlistEnrollments();
  }, []);

  // Persist sort preferences
  useEffect(() => {
    localStorage.setItem('enrollments-sortBy', sortBy);
  }, [sortBy]);

  useEffect(() => {
    localStorage.setItem('enrollments-sortOrder', sortOrder);
  }, [sortOrder]);

  // Filter enrollments
  const filteredEnrollments = enrollments.filter(enrollment => {
    // Handle different property names from backend vs frontend
    const studentName = enrollment.studentName || `${enrollment.studentFirstName || ''} ${enrollment.studentLastName || ''}`.trim();
    const className = enrollment.className || enrollment.class_name || '';
    const instructor = enrollment.instructor || '';
    const parentName = enrollment.parentName || enrollment.parent_name || '';
    
    const matchesSearch = studentName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         className.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         instructor.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         parentName.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStudentName = !filters.studentName || studentName.toLowerCase().includes(filters.studentName.toLowerCase());
    const matchesClassName = !filters.className || className.toLowerCase().includes(filters.className.toLowerCase());
    const matchesInstructor = !filters.instructor || instructor.toLowerCase().includes(filters.instructor.toLowerCase());
    const matchesPaymentStatus = !filters.paymentStatus || enrollment.paymentStatus === filters.paymentStatus;
    const matchesEnrollmentStatus = !filters.enrollmentStatus || enrollment.enrollmentStatus === filters.enrollmentStatus;
    const matchesDateFrom = !filters.dateFrom || enrollment.enrollmentDate >= filters.dateFrom;
    const matchesDateTo = !filters.dateTo || enrollment.enrollmentDate <= filters.dateTo;

    return matchesSearch && matchesStudentName && matchesClassName && matchesInstructor && 
           matchesPaymentStatus && matchesEnrollmentStatus && matchesDateFrom && matchesDateTo;
  });

  // Sort enrollments
  const sortedEnrollments = [...filteredEnrollments].sort((a, b) => {
    const getStudentName = (enrollment) => {
      return enrollment.studentName || `${enrollment.studentFirstName || ''} ${enrollment.studentLastName || ''}`.trim();
    };

    let aValue, bValue;

    switch (sortBy) {
      case 'studentName':
        aValue = getStudentName(a).toLowerCase();
        bValue = getStudentName(b).toLowerCase();
        break;
      case 'className':
        aValue = (a.className || a.class_name || '').toLowerCase();
        bValue = (b.className || b.class_name || '').toLowerCase();
        break;
      case 'instructor':
        aValue = (a.instructor || '').toLowerCase();
        bValue = (b.instructor || '').toLowerCase();
        break;
      case 'paymentStatus':
        const paymentOrder = { 'overdue': 1, 'pending': 2, 'partial': 3, 'paid': 4 };
        aValue = paymentOrder[a.paymentStatus] || 0;
        bValue = paymentOrder[b.paymentStatus] || 0;
        break;
      case 'enrollmentStatus':
        const statusOrder = { 'cancelled': 1, 'completed': 2, 'active': 3 };
        aValue = statusOrder[a.enrollmentStatus] || 0;
        bValue = statusOrder[b.enrollmentStatus] || 0;
        break;
      case 'enrollmentDate':
        aValue = new Date(a.enrollmentDate || 0);
        bValue = new Date(b.enrollmentDate || 0);
        break;
      case 'classPrice':
        aValue = parseFloat(a.classPrice) || 0;
        bValue = parseFloat(b.classPrice) || 0;
        break;
      case 'parentName':
        aValue = (a.parentName || a.parent_name || '').toLowerCase();
        bValue = (b.parentName || b.parent_name || '').toLowerCase();
        break;
      default:
        aValue = new Date(a.enrollmentDate || 0);
        bValue = new Date(b.enrollmentDate || 0);
    }

    if (sortOrder === 'asc') {
      return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
    } else {
      return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
    }
  });

  // Selection handlers
  const handleSelectAll = () => {
    if (selectedEnrollments.length === sortedEnrollments.length) {
      setSelectedEnrollments([]);
    } else {
      setSelectedEnrollments(sortedEnrollments.map(enrollment => enrollment.id));
    }
  };

  const handleSelectEnrollment = (enrollmentId, isSelected) => {
    if (isSelected) {
      setSelectedEnrollments(prev => [...prev, enrollmentId]);
    } else {
      setSelectedEnrollments(prev => prev.filter(id => id !== enrollmentId));
    }
  };

  // Action handlers
  const handleView = (enrollment) => {
    console.log('View enrollment:', enrollment);
    // TODO: Implement enrollment view modal or navigation
  };

  const handleEdit = (enrollment) => {
    console.log('Edit enrollment:', enrollment);
    // Navigate to edit enrollment page
    navigate(`/enrollments/edit/${enrollment.id}`);
  };

  const handleEmail = (enrollment) => {
    const email = enrollment.parentEmail || enrollment.parent_email;
    if (email) {
      window.location.href = `mailto:${email}`;
    }
  };

  const handleDeleteEnrollment = async (enrollment) => {
    if (confirm('Are you sure you want to cancel this enrollment?')) {
      try {
        await cancelEnrollment(enrollment.id);
        setSelectedEnrollments(prev => prev.filter(id => id !== enrollment.id));
      } catch (error) {
        console.error('Failed to cancel enrollment:', error);
      }
    }
  };

  const handleBulkDelete = async () => {
    if (selectedEnrollments.length >= 1 && confirm(`Are you sure you want to cancel ${selectedEnrollments.length} enrollment(s)?`)) {
      try {
        await cancelEnrollments(selectedEnrollments);
        setSelectedEnrollments([]);
      } catch (error) {
        console.error('Failed to cancel enrollments:', error);
      }
    }
  };

  const handleBulkEdit = () => {
    // Navigate to bulk edit page with selected enrollment IDs
    const enrollmentIds = selectedEnrollments.join(',');
    navigate(`/enrollments/bulk-edit?ids=${enrollmentIds}`);
  };

  const clearFilters = () => {
    setSearchTerm('');
    setSortBy('enrollmentDate');
    setSortOrder('desc');
    setFilters({
      studentName: '',
      className: '',
      instructor: '',
      paymentStatus: '',
      enrollmentStatus: 'active', // Reset to active enrollments only
      dateFrom: '',
      dateTo: ''
    });
    // Clear persisted sort preferences when clearing filters
    localStorage.removeItem('enrollments-sortBy');
    localStorage.removeItem('enrollments-sortOrder');
  };

  const activeFiltersCount = [
    searchTerm,
    filters.studentName,
    filters.className,
    filters.instructor,
    filters.paymentStatus,
    filters.enrollmentStatus !== 'active' ? filters.enrollmentStatus : '', // Don't count default "active" filter
    filters.dateFrom,
    filters.dateTo
  ].filter(Boolean).length;

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-lg text-[#3C3C3C] dark:text-gray-100">Loading enrollments...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <UserCheck className="w-8 h-8 text-[#7A99D8]" />
            <div>
              <h1 className="text-lg font-bold text-[#3C3C3C] dark:text-gray-100">Enrollments</h1>
              <p className="text-sm text-gray-600 dark:text-gray-400">{sortedEnrollments.length} enrollments • Manage student registrations</p>
            </div>
          </div>
          <Link
            to="/enrollments/add"
            className="bg-[#FFECC5] hover:bg-[#FFE4A3] text-[#3C3C3C] px-4 py-2 rounded-lg font-medium flex items-center space-x-2 transition-colors"
          >
            <Plus className="w-4 h-4" />
            <span>Add Enrollment</span>
          </Link>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
        <div className="flex px-6">
          <button
            onClick={() => setActiveTab('classes')}
            className={`px-4 py-3 font-medium flex items-center gap-2 border-b-2 transition-colors ${
              activeTab === 'classes'
                ? 'text-[#7A99D8] border-[#7A99D8]'
                : 'text-gray-600 dark:text-gray-400 border-transparent hover:text-[#7A99D8] dark:hover:text-[#7A99D8]'
            }`}
          >
            <Users className="w-4 h-4" />
            Class Enrollments
          </button>
          <button
            onClick={() => setActiveTab('learning-blocks')}
            className={`px-4 py-3 font-medium flex items-center gap-2 border-b-2 transition-colors ${
              activeTab === 'learning-blocks'
                ? 'text-[#7A99D8] border-[#7A99D8]'
                : 'text-gray-600 dark:text-gray-400 border-transparent hover:text-[#7A99D8] dark:hover:text-[#7A99D8]'
            }`}
          >
            <BookOpen className="w-4 h-4" />
            Block Enrollments
          </button>
          <button
            onClick={() => setActiveTab('waitlist')}
            className={`px-4 py-3 font-medium flex items-center gap-2 border-b-2 transition-colors ${
              activeTab === 'waitlist'
                ? 'text-[#7A99D8] border-[#7A99D8]'
                : 'text-gray-600 dark:text-gray-400 border-transparent hover:text-[#7A99D8] dark:hover:text-[#7A99D8]'
            }`}
          >
            <ClipboardList className="w-4 h-4" />
            Waitlist
          </button>
        </div>
      </div>

      <div className="px-6 py-6">
        {activeTab === 'classes' ? (
          <>
        {/* Search and Filters */}
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6 mb-6">
          <div className="space-y-4">
            {/* Search Bar */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search enrollments by student, class, instructor, or parent..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-200 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#7A99D8] focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400"
              />
            </div>

            {/* Filter Toggle */}
            <div className="flex items-center justify-between">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="flex items-center space-x-2 text-sm text-[#3C3C3C] dark:text-gray-100 hover:text-[#7A99D8] dark:hover:text-[#7A99D8] transition-colors"
              >
                <Filter className="w-4 h-4" />
                <span>Filters</span>
                {activeFiltersCount > 0 && (
                  <span className="bg-[#7A99D8] text-white text-xs rounded-full px-2 py-1">
                    {activeFiltersCount}
                  </span>
                )}
              </button>

              {activeFiltersCount > 0 && (
                <button
                  onClick={clearFilters}
                  className="text-sm text-gray-600 dark:text-gray-400 hover:text-[#3C3C3C] dark:hover:text-gray-100 transition-colors"
                >
                  Clear all filters
                </button>
              )}
            </div>

            {/* Expandable Filters */}
            {showFilters && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                <div>
                  <label className="block text-sm font-medium text-[#3C3C3C] dark:text-gray-100 mb-2">Student Name</label>
                  <input
                    type="text"
                    value={filters.studentName}
                    onChange={(e) => setFilters(prev => ({ ...prev, studentName: e.target.value }))}
                    placeholder="Search student name..."
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#7A99D8] focus:border-transparent"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-[#3C3C3C] mb-2">Class Name</label>
                  <input
                    type="text"
                    value={filters.className}
                    onChange={(e) => setFilters(prev => ({ ...prev, className: e.target.value }))}
                    placeholder="Search class name..."
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#7A99D8] focus:border-transparent"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-[#3C3C3C] mb-2">Instructor</label>
                  <input
                    type="text"
                    value={filters.instructor}
                    onChange={(e) => setFilters(prev => ({ ...prev, instructor: e.target.value }))}
                    placeholder="Search instructor..."
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#7A99D8] focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#3C3C3C] mb-2">Payment Status</label>
                  <select
                    value={filters.paymentStatus}
                    onChange={(e) => setFilters(prev => ({ ...prev, paymentStatus: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#7A99D8] focus:border-transparent"
                  >
                    <option value="">All Payment Status</option>
                    <option value="paid">Paid</option>
                    <option value="partial">Partial</option>
                    <option value="pending">Pending</option>
                    <option value="overdue">Overdue</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-[#3C3C3C] mb-2">Enrollment Status</label>
                  <select
                    value={filters.enrollmentStatus}
                    onChange={(e) => setFilters(prev => ({ ...prev, enrollmentStatus: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#7A99D8] focus:border-transparent"
                  >
                    <option value="">All Enrollment Status</option>
                    <option value="active">Active</option>
                    <option value="completed">Completed</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-[#3C3C3C] mb-2">Date Range</label>
                  <div className="grid grid-cols-2 gap-2">
                    <input
                      type="date"
                      value={filters.dateFrom}
                      onChange={(e) => setFilters(prev => ({ ...prev, dateFrom: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#7A99D8] focus:border-transparent"
                      placeholder="From"
                    />
                    <input
                      type="date"
                      value={filters.dateTo}
                      onChange={(e) => setFilters(prev => ({ ...prev, dateTo: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#7A99D8] focus:border-transparent"
                      placeholder="To"
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Bulk Actions */}
        {selectedEnrollments.length > 0 && (
          <div className="bg-[#F5F5F5] dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 mb-6 flex items-center justify-between">
            <span className="text-[#3C3C3C] dark:text-gray-100 font-medium">
              {selectedEnrollments.length} enrollment{selectedEnrollments.length !== 1 ? 's' : ''} selected
            </span>
            <div className="flex items-center gap-2">
              <button
                onClick={handleBulkEdit}
                className="px-3 py-1 bg-[#FFECC5] hover:bg-[#FFE4A3] text-[#3C3C3C] rounded-md flex items-center gap-1 text-sm font-medium"
              >
                <Edit3 className="w-3 h-3" />
                Edit Selected
              </button>
              <button
                onClick={handleBulkDelete}
                className="px-3 py-1 bg-red-600 text-white rounded-md hover:bg-red-700 flex items-center gap-1 text-sm"
              >
                <Trash2 className="w-3 h-3" />
                Cancel Selected
              </button>
              <button
                onClick={() => setSelectedEnrollments([])}
                className="p-1 text-[#3C3C3C] dark:text-gray-100 hover:text-gray-600 dark:hover:text-gray-400"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}

        {/* Results Summary and Sorting */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <span className="text-gray-600 dark:text-gray-400">
              Showing {sortedEnrollments.length} of {enrollments.length} enrollments
            </span>
            {sortedEnrollments.length > 0 && (
              <button
                onClick={handleSelectAll}
                className="text-sm text-[#7A99D8] hover:text-[#3C3C3C] dark:hover:text-gray-100 font-medium"
              >
                {selectedEnrollments.length === sortedEnrollments.length ? 'Deselect all' : 'Select all'}
              </button>
            )}
          </div>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-[#3C3C3C] dark:text-gray-100">Sort by:</label>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="px-3 py-1 border border-gray-200 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#7A99D8] focus:border-transparent text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              >
                <option value="enrollmentDate">Enrollment Date</option>
                <option value="studentName">Student Name</option>
                <option value="className">Class Name</option>
                <option value="instructor">Instructor</option>
                <option value="paymentStatus">Payment Status</option>
                <option value="enrollmentStatus">Enrollment Status</option>
                <option value="classPrice">Class Price</option>
                <option value="parentName">Parent Name</option>
              </select>
            </div>
            <button
              onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
              className="flex items-center gap-1 text-sm text-[#3C3C3C] dark:text-gray-100 hover:text-[#7A99D8] dark:hover:text-[#7A99D8] transition-colors"
            >
              {sortOrder === 'asc' ? '↑' : '↓'} {sortOrder === 'asc' ? 'A-Z' : 'Z-A'}
            </button>
          </div>
        </div>

        {/* Enrollments List */}
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
          {sortedEnrollments.length === 0 ? (
            <div className="p-12 text-center">
              <UserCheck className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-[#3C3C3C] dark:text-gray-100 mb-2">
                {enrollments.length === 0 ? 'No enrollments yet' : 'No enrollments match your filters'}
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
                {enrollments.length === 0 
                  ? 'Get started by creating your first enrollment.'
                  : 'Try adjusting your search terms or filters to find what you\'re looking for.'
                }
              </p>
              <Link
                to="/enrollments/add"
                className="bg-[#FFECC5] hover:bg-[#FFE4A3] text-[#3C3C3C] px-6 py-3 rounded-lg inline-flex items-center gap-2 transition-colors font-medium"
              >
                <Plus className="w-5 h-5" />
                Create Your First Enrollment
              </Link>
            </div>
          ) : (
            <div className="divide-y divide-gray-200 dark:divide-gray-700">
              {/* List Header - Hidden on mobile */}
              <div className="hidden lg:block px-4 py-2 bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700">
                <div className="flex items-center">
                  <div className="w-4 mr-3"></div> {/* Checkbox space */}
                  <div className="w-8 mr-3"></div> {/* Photo space */}
                  <div className="w-32 mr-4 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Student</div>
                  <div className="flex-1 mr-4 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Class & Dates</div>
                  <div className="w-24 mr-4 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Status</div>
                  <div className="w-24 mr-4 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Payment</div>
                  <div className="w-20 mr-4 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Amount</div>
                  <div className="w-20 mr-4 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Enrolled</div>
                  <div className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Actions</div>
                </div>
              </div>

              {/* Enrollment Rows */}
              {sortedEnrollments.map(enrollment => (
                <EnrollmentRow
                  key={enrollment.id}
                  enrollment={enrollment}
                  isSelected={selectedEnrollments.includes(enrollment.id)}
                  onSelect={handleSelectEnrollment}
                  onView={handleView}
                  onEdit={handleEdit}
                  onEmail={handleEmail}
                  onDelete={handleDeleteEnrollment}
                />
              ))}
            </div>
          )}
        </div>
          </>
        ) : activeTab === 'learning-blocks' ? (
          /* Track Enrollments Tab */
          <div className="space-y-6">
            {blockEnrollmentsLoading ? (
              <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-12 text-center">
                <div className="text-lg text-[#3C3C3C] dark:text-gray-100">Loading track enrollments...</div>
              </div>
            ) : blockEnrollments.length === 0 ? (
              <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-12 text-center">
                <BookOpen className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-[#3C3C3C] dark:text-gray-100 mb-2">
                  No track enrollments yet
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
                  Block enrollments will appear here when students are enrolled in blocks.
                </p>
              </div>
            ) : (
              <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                <div className="divide-y divide-gray-200 dark:divide-gray-700">
                  {/* Table Header */}
                  <div className="px-4 py-3 bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700">
                    <div className="grid grid-cols-12 gap-4 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      <div className="col-span-3">Student</div>
                      <div className="col-span-3">Track</div>
                      <div className="col-span-2">Pricing Option</div>
                      <div className="col-span-2">Amount</div>
                      <div className="col-span-1">Status</div>
                      <div className="col-span-1">Actions</div>
                    </div>
                  </div>

                  {/* Track Enrollment Rows */}
                  {blockEnrollments.map((enrollment) => {
                    const studentName = enrollment.studentName ||
                      `${enrollment.student_first_name || ''} ${enrollment.student_last_name || ''}`.trim();
                    const trackTitle = enrollment.trackTitle || enrollment.track_title || 'N/A';
                    const pricingOption = enrollment.pricingOption || enrollment.pricing_option || 'N/A';
                    const amount = enrollment.amount || 0;
                    const status = enrollment.status || 'active';

                    const getStatusColor = (status) => {
                      switch (status) {
                        case 'active': return 'bg-emerald-100 text-emerald-700';
                        case 'completed': return 'bg-blue-100 text-blue-700';
                        case 'cancelled': return 'bg-red-100 text-red-700';
                        default: return 'bg-gray-100 text-gray-700';
                      }
                    };

                    return (
                      <div key={enrollment.id} className="px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
                        <div className="grid grid-cols-12 gap-4 items-center">
                          <div className="col-span-3">
                            <div className="flex items-center gap-3">
                              {enrollment.student_photo ? (
                                <img
                                  src={enrollment.student_photo}
                                  alt={studentName}
                                  className="w-8 h-8 rounded-lg object-cover"
                                />
                              ) : (
                                <div className="w-8 h-8 bg-gradient-to-br from-blue-300 to-blue-500 rounded-lg flex items-center justify-center text-white text-xs font-medium">
                                  {(enrollment.student_first_name || '').charAt(0)}{(enrollment.student_last_name || '').charAt(0)}
                                </div>
                              )}
                              <span className="text-sm font-medium text-[#3C3C3C] dark:text-gray-100">{studentName}</span>
                            </div>
                          </div>
                          <div className="col-span-3">
                            <span className="text-sm text-gray-900 dark:text-gray-100">{trackTitle}</span>
                          </div>
                          <div className="col-span-2">
                            <span className="text-sm text-gray-600 dark:text-gray-400 capitalize">{pricingOption}</span>
                          </div>
                          <div className="col-span-2">
                            <span className="text-sm font-medium text-gray-900 dark:text-gray-100">${parseFloat(amount).toFixed(2)}</span>
                          </div>
                          <div className="col-span-1">
                            <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(status)}`}>
                              {status.charAt(0).toUpperCase() + status.slice(1)}
                            </span>
                          </div>
                          <div className="col-span-1">
                            <button
                              onClick={() => {
                                // Navigate to track view
                                navigate(`/tracks/view/${enrollment.track_id || enrollment.trackId}`);
                              }}
                              className="p-1.5 text-gray-400 dark:text-gray-500 hover:text-[#7A99D8] dark:hover:text-[#7A99D8] rounded hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                              title="View Track"
                            >
                              <Eye className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        ) : (
          /* Waitlist Tab */
          <div className="space-y-6">
            {waitlistLoading ? (
              <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-12 text-center">
                <div className="text-lg text-[#3C3C3C] dark:text-gray-100">Loading waitlist...</div>
              </div>
            ) : waitlistEnrollments.length === 0 ? (
              <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-12 text-center">
                <ClipboardList className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-[#3C3C3C] dark:text-gray-100 mb-2">
                  No waitlist entries yet
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
                  Waitlist entries will appear here when students join the waitlist for full classes.
                </p>
              </div>
            ) : (
              <div className="space-y-6">
                {/* Group waitlist entries by class */}
                {(() => {
                  // Group entries by class
                  const groupedByClass = waitlistEnrollments.reduce((acc, entry) => {
                    const classId = entry.class_id;
                    if (!acc[classId]) {
                      acc[classId] = {
                        className: entry.class_title || 'Untitled Class',
                        entries: []
                      };
                    }
                    acc[classId].entries.push(entry);
                    return acc;
                  }, {});

                  return Object.values(groupedByClass).map((group, groupIndex) => (
                    <div key={groupIndex} className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                      {/* Class Header */}
                      <div className="px-4 py-3 bg-gray-100 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700">
                        <div className="flex items-center justify-between">
                          <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">{group.className}</h3>
                          <span className="text-sm text-gray-600 dark:text-gray-400">
                            {group.entries.length} on waitlist
                          </span>
                        </div>
                      </div>

                      <div className="divide-y divide-gray-200 dark:divide-gray-700">
                        {/* Table Header */}
                        <div className="px-4 py-3 bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
                          <div className="grid grid-cols-11 gap-4 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                            <div className="col-span-2">Position</div>
                            <div className="col-span-4">Student</div>
                            <div className="col-span-3">Joined Date</div>
                            <div className="col-span-1">Notified</div>
                            <div className="col-span-1">Actions</div>
                          </div>
                        </div>

                        {/* Waitlist Entries for this class */}
                        {group.entries.map((entry) => {
                          const studentName = `${entry.student_first_name || ''} ${entry.student_last_name || ''}`.trim();
                          const joinedDate = entry.waitlist_date ? new Date(entry.waitlist_date).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric'
                          }) : 'N/A';
                          const position = entry.position || '—';
                          const notified = entry.notified || false;

                          return (
                            <div key={entry.id} className="px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
                              <div className="grid grid-cols-11 gap-4 items-center">
                                <div className="col-span-2">
                                  <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-[#7A99D8] text-white text-sm font-semibold">
                                    #{position}
                                  </span>
                                </div>
                                <div className="col-span-4">
                                  <div className="flex items-center gap-3">
                                    <div className="w-8 h-8 bg-gradient-to-br from-gray-300 to-gray-500 rounded-lg flex items-center justify-center text-white text-xs font-medium">
                                      {(entry.student_first_name || '').charAt(0)}{(entry.student_last_name || '').charAt(0)}
                                    </div>
                                    <div>
                                      <span className="text-sm font-medium text-[#3C3C3C] dark:text-gray-100 block">{studentName}</span>
                                      <span className="text-xs text-gray-500 dark:text-gray-400">{entry.parent_email}</span>
                                    </div>
                                  </div>
                                </div>
                                <div className="col-span-3">
                                  <span className="text-sm text-gray-600 dark:text-gray-400">{joinedDate}</span>
                                </div>
                                <div className="col-span-1">
                                  {notified ? (
                                    <CheckCircle className="w-4 h-4 text-emerald-600" />
                                  ) : (
                                    <XCircle className="w-4 h-4 text-gray-300" />
                                  )}
                                </div>
                                <div className="col-span-1">
                                  <button
                                    onClick={async () => {
                                      if (confirm(`Remove ${studentName} from the waitlist?`)) {
                                        try {
                                          await api.request(`/enrollments/waitlist/${entry.id}`, { method: 'DELETE' });
                                          fetchWaitlistEnrollments(); // Refresh the list
                                        } catch (err) {
                                          console.error('Error removing from waitlist:', err);
                                        }
                                      }
                                    }}
                                    className="p-1.5 text-gray-400 dark:text-gray-500 hover:text-red-600 dark:hover:text-red-400 rounded hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                                    title="Remove from Waitlist"
                                  >
                                    <Trash2 className="w-3.5 h-3.5" />
                                  </button>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ));
                })()}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Enrollments;